import { FC, useEffect, useState } from "react";
import {
  AccordionHeader,
  AccordionHeaderText,
  ITabs,
  MuiBox,
  MuiLink,
  MuiModal,
  MuiTabPanel,
  MuiTabs,
  WarningConfirmationModal,
} from "../../../../components";

import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { UpdateProgramParams } from "../../../../api/services/program/models";
import { useUpdateProgramMutation } from "../../../../api/services/program/programApis";
import { ReactComponent as EditIcon } from "../../../../assets/images/edit.svg";
import PermissionWrapper from "../../../../components/permissionWrapper/PermissionWrapper";
import {
  EMPTY_VALUE_DASH,
  MODULE_PERMISSIONS,
} from "../../../../helpers/constants";
import { getValueOrBlank, isNotEmpty } from "../../../../helpers/utils";
import useAccordion from "../../../../hooks/useAccordion";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { CancelFieldModal } from "../../../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import {
  CreateProgramFormKey,
  ProgramDetail,
} from "../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateProgramKeyInput } from "../createProgramKeyInput/CreateProgramKeyInput";
import { CreateProgramGeneralDetailWrapper } from "./CreateProgramGeneralDetail.style";
import { KeyDetail } from "./inputDetailTab/keyDetail/KeyDetail";
import { RegistrationDetail } from "./inputDetailTab/registrationDetail/RegistrationDetail";

interface CreateProgramKeyInputDetailProps {
  pageId: string;
  programId?: string;
  programDetail?: ProgramDetail;
  isViewProgram?: boolean;
  refetchProgramDetail?: (id: string) => void;
  isEditProgram?: boolean;
  isFromEditProjectChildPrograms?: boolean;
  onNavigateToProjectCallBack?: (projectId: string) => void;
}

const accordionStatus = {
  programDetail: true,
};
export const CreateProgramGeneralDetail: FC<
  CreateProgramKeyInputDetailProps
> = ({
  pageId,
  programDetail,
  isViewProgram = false,
  programId = "",
  refetchProgramDetail,
  isEditProgram = false,
  isFromEditProjectChildPrograms,
  onNavigateToProjectCallBack,
}) => {
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(0);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [showModifyConfirmationModal, setShowModifyConfirmationModal] =
    useState(false);

  const { fetchLabel, fetchComponentTitle, commonLabels } = useSiteCore();
  const { handleIndividualAccordionChange } = useAccordion();
  const {
    reset,
    handleSubmit,
    getValues,
    setError,
    trigger,
    formState: { isDirty, dirtyFields, errors },
  } = useFormContext();
  const {
    mutateAsync: updateProgramApi,
    isPending: isUpdatePending,
    isError: isUpdateError,
    isSuccess: isUpdateSuccess,
    error: updateProgramError,
  } = useUpdateProgramMutation();

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.generalDetails, key);
  };

  const tabIdProgramDetails = `${pageId}-tab1`;
  const tabIdRegistrationDetails = `${pageId}-tab2`;
  const tabs: ITabs[] = [
    {
      id: tabIdProgramDetails,
      label: getLabel(programSiteCoreKeys.generalKeyProgramDetails),
    },
    ...(isNotEmpty(programDetail?.registrationCode) && (isEditProgram || isViewProgram)
      ? [
          {
            id: tabIdRegistrationDetails,
            label: getLabel(programSiteCoreKeys.registrationDetails),
          },
        ]
      : []),
  ];

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTab(value);
  };

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const openModifyModal = () => {
    setShowModifyModal(true);
    if (isNotEmpty(programDetail)) {
      const defaultValues = {
        [CreateProgramFormKey.client]: programDetail.clientId,
        [CreateProgramFormKey.deliveryCountry]: programDetail.deliveryCountry,
        [CreateProgramFormKey.lineOfBusiness]: programDetail.lineOfBusinessId,
        [CreateProgramFormKey.programTemplate]: programDetail.programTemplateId,
        [CreateProgramFormKey.alternateName]:
          programDetail.alternateDisplayName ?? EMPTY_VALUE_DASH,
        [CreateProgramFormKey.salesOfficeId]: getValueOrBlank(
          programDetail.salesOfficeId,
        ),
        [CreateProgramFormKey.isProjectTagged]:
          programDetail.isProjectTagged ?? false,
        [CreateProgramFormKey.projectKey]: programDetail.projectKey ?? "",
        [CreateProgramFormKey.projectName]: programDetail.projectName ?? "",
        [CreateProgramFormKey.nextPersona]:
          programDetail.nextPersona ?? EMPTY_VALUE_DASH,
        [CreateProgramFormKey.opportunityId]: getValueOrBlank(
          programDetail?.opportunityId,
        ),
        [CreateProgramFormKey.opportunityName]: getValueOrBlank(
          programDetail?.opportunityName,
        ),
      };
      if (isDirty) {
        reset(getValues());
      } else {
        reset(defaultValues);
      }
    }
  };

  useEffect(() => {
    if (isUpdateError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isUpdateError]);

  useEffect(() => {
    if (isUpdatePending) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isUpdatePending]);

  useEffect(() => {
    if (isUpdateSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            programSiteCoreKeys.keyProgramDetails,
            programSiteCoreKeys.programUpdatedSuccessMessage,
          ),
        }),
      );
      setShowModifyModal(false);
      refetchProgramDetail?.(programId);
    }
  }, [isUpdateSuccess]);

  const checkAndValidateOpportunityId = (): boolean => {
    const { opportunityId, isOpportunityIdValidated } = getValues();
    if (isNotEmpty(opportunityId) && !isOpportunityIdValidated) {
      if (!errors?.opportunityId) {
        setError(CreateProgramFormKey.opportunityId, {
          type: "custom",
          message: fetchLabel(
            programSiteCoreKeys.keyProgramDetails,
            programSiteCoreKeys.opportunityIDMustBeValidatedMessage,
          ),
        });
      }
      return false;
    }
    return true;
  };

  const checkValidationAndUpdate = () => {
    if (!checkAndValidateOpportunityId()) {
      trigger([
        "client",
        "deliveryCountry",
        "lineOfBusiness",
        "programTemplate",
      ]);
      return;
    }
    trigger().then((isValid) => {
      if (isValid) {
        const { lineOfBusiness, programTemplate } = dirtyFields;
        if (lineOfBusiness || programTemplate) {
          setShowModifyConfirmationModal(true);
          setShowModifyModal(false);
        } else {
          handleSubmit(handleUpdateDetail)();
        }
      }
    });
  };

  const handleUpdateDetail = () => {
    const {
      client,
      deliveryCountry,
      lineOfBusiness,
      programTemplate,
      alternateName,
      isProjectTagged,
      projectKey,
      nextPersona,
      opportunityId,
      opportunityName,
    } = getValues();

    const updateProgramParams: UpdateProgramParams = {
      id: programId,
      clientId: client,
      alternateDisplayName: alternateName,
      countryId: deliveryCountry,
      lobId: lineOfBusiness,
      programTemplateId: programTemplate,
      salesOffice: getValueOrBlank(programDetail?.salesOffice),
      keyNumber: getValueOrBlank(programDetail?.keyNumber),
      isProjectTagged: isProjectTagged,
      projectId: programDetail?.projectId
        ? programDetail?.projectId
        : isProjectTagged && isNotEmpty(projectKey)
          ? projectKey
          : "",
      isValidateName: false,
      nextPersona: getValueOrBlank(nextPersona),
      opportunityId: getValueOrBlank(opportunityId),
      opportunityName: getValueOrBlank(opportunityName),
    };
    updateProgramApi(updateProgramParams);
  };

  const handleCancelOk = () => {
    setShowModifyModal(false);
    setShowCancelModal(false);
  };

  const handleCancelClick = () => {
    if (isDirty) {
      setShowCancelModal(true);
    } else {
      setShowModifyModal(false);
    }
  };

  const handleModifyConfirmationModalClose = () => {
    if (showModifyConfirmationModal) {
      setShowModifyConfirmationModal(false);
      openModifyModal();
    }
  };

  const handleModifyConfirmationClick = () => {
    if (showModifyConfirmationModal) {
      setShowModifyConfirmationModal(false);
      handleSubmit(handleUpdateDetail)();
    }
  };

  return (
    <CreateProgramGeneralDetailWrapper>
      <PermissionWrapper
        permission={MODULE_PERMISSIONS.VIEW_KEY_PROGRAM_DETAIL}
      >
        <AccordionHeader
          title={renderHeader(
            fetchComponentTitle(programSiteCoreKeys.generalDetails),
          )}
          onChange={() => handleIndividualAccordionChange("programDetail")}
          isExpanded={accordionStatus.programDetail}
        >
          <MuiTabs
            tabs={tabs}
            value={selectedTab}
            onChange={onTabChange}
            tabsTestIds={[tabIdProgramDetails, tabIdRegistrationDetails]}
          >
            <MuiTabPanel
              panelId={`${pageId}-panel1`}
              value={selectedTab}
              index={0}
              ariaLabelledById={tabIdProgramDetails}
            >
              {!isViewProgram && (
                <PermissionWrapper
                  permission={MODULE_PERMISSIONS.EDIT_KEY_PROGRAM_DETAIL}
                >
                  <MuiBox className="modify-detail">
                    <MuiLink
                      className="mui-link link"
                      onClick={openModifyModal}
                      id="modify-link"
                      data-testid="modify-link"
                    >
                      <EditIcon />
                      {commonLabels.modify}
                    </MuiLink>
                  </MuiBox>
                </PermissionWrapper>
              )}
              <KeyDetail
                programDetail={programDetail}
                displayKeyDetails={isEditProgram || isViewProgram}
                isFromEditProjectChildPrograms={isFromEditProjectChildPrograms}
                onNavigateToProjectCallBack={onNavigateToProjectCallBack}
              />
            </MuiTabPanel>
            <MuiTabPanel
              panelId={`${pageId}-panel2`}
              value={selectedTab}
              index={1}
              ariaLabelledById={tabIdRegistrationDetails}
            >
              <RegistrationDetail programDetail={programDetail} />
            </MuiTabPanel>
          </MuiTabs>
          {showModifyModal && (
            <MuiModal
              onClose={handleCancelClick}
              title={`${fetchLabel(
                programSiteCoreKeys.keyProgramDetails,
                programSiteCoreKeys.modifyKeyProgramDetails,
              )} - ${programDetail?.keyNumber ?? ""}`}
              open={showModifyModal}
              hasLargeContent
              primaryButtonTitle={commonLabels.update}
              onPrimaryButtonClick={checkValidationAndUpdate}
              secondaryButtonTitle={commonLabels.cancel}
              onSecondaryButtonClick={handleCancelClick}
            >
              <CreateProgramKeyInput
                pageId={pageId}
                isUpdateDetail={true}
                programDetail={programDetail}
                apiErrorData={updateProgramError}
                programId={programId}
              />
            </MuiModal>
          )}
          {showCancelModal && (
            <CancelFieldModal
              showModal={showCancelModal}
              handleModalClose={() => setShowCancelModal(false)}
              handlePrimaryButtonClick={handleCancelOk}
            />
          )}
          {showModifyConfirmationModal && (
            <WarningConfirmationModal
              show={showModifyConfirmationModal}
              modalTitle={commonLabels.confirmation}
              onClose={handleModifyConfirmationModalClose}
              secondaryButtonTitle={commonLabels.no}
              onPrimaryButtonClick={handleModifyConfirmationClick}
              labelContent={fetchLabel(
                programSiteCoreKeys.keyProgramDetails,
                programSiteCoreKeys.areYouSureYouWantToModifyTheKeyProgramDetails,
              )}
            />
          )}
        </AccordionHeader>
      </PermissionWrapper>
    </CreateProgramGeneralDetailWrapper>
  );
};
